import OT, { Publisher } from "@opentok/client";

import { Dispatch, SetStateAction, useEffect } from "react";

const useScreenShareListener = (
  session: (OT.Session & { streams?: OT.Stream[] }) | null,
  setShareScreen: Dispatch<
    SetStateAction<{ isSharing: boolean; publisher: Publisher | null }>
  >
) => {
  useEffect(() => {
    if (!session) return;

    const handleStreamCreated = (event: any) => {
      const { stream } = event;

      // Only subscribe to screen shares from other participants
      if (
        stream.videoType !== "screen" ||
        stream.connection.connectionId === session.connection?.connectionId
      ) {
        return;
      }

      let screenContainer = document.getElementById("screen-preview");
      if (!screenContainer) {
        const publisherWrapper = document.getElementById(
          "meeting-room-videos-area"
        );
        if (!publisherWrapper) return;

        screenContainer = document.createElement("div");
        screenContainer.id = "screen-preview";
        screenContainer.style.width = "100%";
        screenContainer.style.height = "100%";
        screenContainer.style.position = "absolute";
        publisherWrapper.appendChild(screenContainer);
      }

      session.subscribe(
        stream,
        screenContainer.id,
        {
          insertMode: "append",
          width: "100%",
          height: "100%",
          showControls: false,
        },
        (error) => {
          if (error) {
            console.error("Error subscribing to screen share:", error);
            // Optionally remove the screenContainer if subscription fails for some reason
            if (screenContainer) screenContainer.remove();
          } else {
            // Set isSharing to true as soon as we successfully subscribe to a remote screen share
            setShareScreen((prev) => ({ ...prev, isSharing: true }));
            console.log("Subscribed to remote screen share.");
          }
        }
      );
    };

    const handleStreamDestroyed = (event: any) => {
      const { stream } = event;

      // If it's a screen share stream and it's from another participant, clean up
      if (
        stream.videoType === "screen" &&
        stream.connection.connectionId !== session.connection?.connectionId
      ) {
        // Find the subscriber element and remove it
        const screenContainer = document.getElementById("screen-preview");
        if (screenContainer) {
          screenContainer.remove();
        }

        // Immediately set isSharing to false because this screen share is gone.
        // No need to check session.streams here, as the stream is already destroyed.
        setShareScreen((prev) => ({ ...prev, isSharing: false }));
        console.log("Remote screen share stream destroyed.");
      }
    };

    const subscribeToExistingScreenShares = () => {
      session.streams?.forEach((stream) => {
        if (
          stream.videoType === "screen" &&
          stream.connection.connectionId !== session.connection?.connectionId
        ) {
          handleStreamCreated({
            type: "streamCreated",
            stream,
            target: session,
          }); // Simulate event structure
        }
      });
    };

    session.on("streamCreated", handleStreamCreated);
    session.on("streamDestroyed", handleStreamDestroyed);

    session.on("sessionConnected", subscribeToExistingScreenShares);

    // If already connected when this hook mounts, try to subscribe to existing streams
    if (session.connection) {
      subscribeToExistingScreenShares();
    }

    return () => {
      session.off("streamCreated", handleStreamCreated);
      session.off("streamDestroyed", handleStreamDestroyed);
      session.off("sessionConnected", subscribeToExistingScreenShares);
    };
  }, [session, setShareScreen]);
};

export default useScreenShareListener;
